export interface ApiUser {
  id: string;
  name: string;
  email: string;
  role: "superadmin" | "admin" | "hr" | "manager" | "employee";
  department: string;
  avatar: string;
  phone?: string;
  is_active?: number;
  joinedAt?: string; // Mapped from created_at
}

export interface CreateUserData {
  email: string;
  password: string;
  name: string;
  department?: string;
  role?: "superadmin" | "admin" | "hr" | "manager" | "employee";
  phone?: string;
  avatar?: string;
}

export interface UpdateUserData {
  name?: string;
  phone?: string;
  avatar?: string;
  department?: string;
  role?: "superadmin" | "admin" | "hr" | "manager" | "employee";
}

const API_BASE = import.meta.env.VITE_API_URL || "";

class FlowHRAPI {
  private token: string | null = null;
  private initialized = false;

  constructor() {
    // Safe initialization - only read localStorage if available
    try {
      if (typeof localStorage !== "undefined") {
        this.token = localStorage.getItem("auth_token");
        console.log(
          "[API] Initialized with token:",
          this.token ? "Present" : "Not found",
        );
      }
      this.initialized = true;
    } catch (error) {
      console.error(
        "[API] Failed to initialize: localStorage not available",
        error,
      );
      this.initialized = false;
    }
  }

  // Public method to refresh token from localStorage (useful after login/reload)
  refreshToken() {
    try {
      if (typeof localStorage !== "undefined") {
        this.token = localStorage.getItem("auth_token");
        console.log(
          "[API] Token refreshed:",
          this.token ? "Present" : "Not found",
        );
      }
    } catch (error) {
      console.error("[API] Failed to refresh token", error);
    }
  }

  // Check if a token is currently set
  hasToken(): boolean {
    return !!this.token;
  }

  // Set token manually and persist to localStorage
  setToken(token: string | null) {
    this.token = token;
    try {
      if (typeof localStorage !== "undefined") {
        if (token) {
          localStorage.setItem("auth_token", token);
          console.log("[API] Token set and persisted");
        } else {
          localStorage.removeItem("auth_token");
          console.log("[API] Token removed");
        }
      }
    } catch (error) {
      console.error("[API] Failed to persist token", error);
    }
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    // Always refresh token from localStorage before making a request
    // This ensures we have the latest token even after page reloads
    if (typeof localStorage !== "undefined") {
      this.token = localStorage.getItem("auth_token");
    }

    const headers = {
      "Content-Type": "application/json",
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    };

    console.log(
      "[API] Request to:",
      endpoint,
      "with token:",
      this.token ? "Present" : "Missing",
    );

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Dispatch logout event for app to handle
        window.dispatchEvent(new CustomEvent("auth:logout"));
        // Also clear local token immediately
        this.token = null;
        localStorage.removeItem("auth_token");
      }
      const error = await response.json();
      // Handle nested error structure: { error: { code, message } } or { error: "message" }
      const errorMessage = error.error?.message || error.error || error.message || "API request failed";
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Helper to unwrap response data if wrapped in standard { success: true, data: ... } envelope
  private getData(response: any) {
    if (response && typeof response === "object" && "data" in response) {
      return response.data;
    }
    return response;
  }

  // Authentication
  async login(email: string, password: string) {
    const response = await this.request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    // Handle response structure: { status, data: { token, user }, message }
    const data = this.getData(response);
    const token = data?.token || response.token;
    if (token) {
      this.token = token;
      localStorage.setItem("auth_token", token);
    }
    // Return the user data for the app
    return data || response;
  }

  async register(userData: any) {
    return this.request("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async logout() {
    this.token = null;
    localStorage.removeItem("auth_token");
  }

  async forgotPassword(email: string) {
    return this.request("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string) {
    return this.request("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // Users
  async getUsers(): Promise<ApiUser[]> {
    const response = await this.request("/api/users");
    const users = this.getData(response);
    if (!Array.isArray(users)) return [];

    return users.map((u: any) => ({
      ...u,
      joinedAt: u.created_at
        ? new Date(u.created_at).toISOString()
        : new Date().toISOString(),
    }));
  }

  async getUser(id: string): Promise<ApiUser> {
    const response = await this.request(`/api/users/${id}`);
    const user = this.getData(response);
    return {
      ...user,
      joinedAt: user.created_at
        ? new Date(user.created_at).toISOString()
        : new Date().toISOString(),
    };
  }

  async createUser(userData: CreateUserData): Promise<ApiUser> {
    const response = await this.request("/api/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
    return this.getData(response);
  }

  async updateUser(id: string, userData: UpdateUserData) {
    return this.request(`/api/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  async updateUserRole(id: string, role: string) {
    return this.request(`/api/users/${id}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    });
  }

  async deleteUser(id: string) {
    return this.request(`/api/users/${id}`, {
      method: "DELETE",
    });
  }

  async activateUser(id: string) {
    return this.request(`/api/users/${id}/activate`, {
      method: "PUT",
    });
  }

  async changePassword(
    id: string,
    currentPassword: string,
    newPassword: string,
  ) {
    return this.request(`/api/users/${id}/password`, {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async adminResetPassword(id: string, newPassword: string) {
    return this.request(`/api/users/${id}/password`, {
      method: "PUT",
      body: JSON.stringify({ newPassword }),
    });
  }

  // Attendance
  async getAttendance() {
    const response = await this.request("/api/attendance");
    return this.getData(response);
  }

  async getTeamAttendance(options?: {
    department?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.department && options.department !== "all") {
      params.append("department", options.department);
    }
    if (options?.startDate) {
      params.append("start_date", options.startDate);
    }
    if (options?.endDate) {
      params.append("end_date", options.endDate);
    }
    const queryString = params.toString();
    const response = await this.request(
      `/api/attendance/team${queryString ? `?${queryString}` : ""}`,
    );
    return this.getData(response);
  }

  async checkIn(location: string, date?: string) {
    return this.request("/api/attendance/checkin", {
      method: "POST",
      body: JSON.stringify({ location, date }),
    });
  }

  async checkOut(date?: string) {
    return this.request("/api/attendance/checkout", {
      method: "POST",
      body: date ? JSON.stringify({ date }) : undefined,
    });
  }

  // Leave Requests
  async getLeaveRequests() {
    const response = await this.request("/api/leave");
    return this.getData(response);
  }

  async createLeaveRequest(leaveData: any) {
    return this.request("/api/leave", {
      method: "POST",
      body: JSON.stringify(leaveData),
    });
  }

  async updateLeaveStatus(
    id: string,
    status: "approved" | "rejected",
    reviewNotes?: string,
  ) {
    return this.request(`/api/leave/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status, reviewNotes }),
    });
  }

  async deleteLeaveRequest(id: string) {
    return this.request(`/api/leave/${id}`, {
      method: "DELETE",
    });
  }

  // Timesheets
  async getTimesheets(week?: string) {
    const query = week ? `?week=${week}` : "";
    const response = await this.request(`/api/timesheet${query}`);
    return this.getData(response);
  }

  async createTimesheetEntry(entry: any) {
    return this.request("/api/timesheet", {
      method: "POST",
      body: JSON.stringify(entry),
    });
  }

  async updateTimesheetEntry(id: string, entry: any) {
    return this.request(`/api/timesheet/${id}`, {
      method: "PUT",
      body: JSON.stringify(entry),
    });
  }

  async submitTimesheet(id: string) {
    return this.request(`/api/timesheet/${id}/submit`, {
      method: "PUT",
    });
  }

  async submitTimesheetWeek(weekStartDate: string) {
    return this.request("/api/timesheet/submit-week", {
      method: "POST",
      body: JSON.stringify({ weekStartDate }),
    });
  }

  async reviewTimesheet(
    id: string,
    status: "approved" | "rejected",
    reviewNotes?: string,
  ) {
    return this.request(`/api/timesheet/${id}/review`, {
      method: "PUT",
      body: JSON.stringify({ status, reviewNotes }),
    });
  }

  async deleteTimesheetEntry(id: string) {
    return this.request(`/api/timesheet/${id}`, {
      method: "DELETE",
    });
  }

  // Notifications
  async getNotifications() {
    const response = await this.request("/api/notifications");
    return this.getData(response);
  }

  async markNotificationRead(id: string) {
    return this.request(`/api/notifications/${id}/read`, {
      method: "PUT",
    });
  }

  // ============== SHIFT MANAGEMENT ==============
  async getShifts(isActive?: boolean) {
    const query = isActive !== undefined ? `?is_active=${isActive}` : "";
    const response = await this.request(`/api/shifts${query}`);
    return this.getData(response);
  }

  async createShift(shiftData: {
    name: string;
    start_time: string;
    end_time: string;
    description?: string;
  }) {
    const response = await this.request("/api/shifts", {
      method: "POST",
      body: JSON.stringify(shiftData),
    });
    return this.getData(response);
  }

  async updateShift(
    id: string,
    shiftData: {
      name?: string;
      start_time?: string;
      end_time?: string;
      description?: string;
      is_active?: number;
    },
  ) {
    const response = await this.request(`/api/shifts/${id}`, {
      method: "PUT",
      body: JSON.stringify(shiftData),
    });
    return this.getData(response);
  }

  async deleteShift(id: string) {
    const response = await this.request(`/api/shifts/${id}`, {
      method: "DELETE",
    });
    return this.getData(response);
  }

  // ============== SHIFT ASSIGNMENTS ==============
  async getShiftAssignments(filters?: {
    user_id?: string;
    shift_schedule_id?: string;
    is_active?: boolean;
  }) {
    const params = new URLSearchParams();
    if (filters?.user_id) params.append("user_id", filters.user_id);
    if (filters?.shift_schedule_id)
      params.append("shift_schedule_id", filters.shift_schedule_id);
    if (filters?.is_active !== undefined)
      params.append("is_active", String(filters.is_active));

    const queryString = params.toString();
    const response = await this.request(
      `/api/shift-assignments${queryString ? `?${queryString}` : ""}`,
    );
    return this.getData(response);
  }

  async createShiftAssignment(assignmentData: {
    user_id: string;
    shift_schedule_id: string;
    effective_from: string;
    effective_until?: string;
  }) {
    const response = await this.request("/api/shift-assignments", {
      method: "POST",
      body: JSON.stringify(assignmentData),
    });
    return this.getData(response);
  }

  async updateShiftAssignment(
    id: string,
    assignmentData: {
      effective_from?: string;
      effective_until?: string;
      is_active?: number;
    },
  ) {
    const response = await this.request(`/api/shift-assignments/${id}`, {
      method: "PUT",
      body: JSON.stringify(assignmentData),
    });
    return this.getData(response);
  }

  async deleteShiftAssignment(id: string) {
    const response = await this.request(`/api/shift-assignments/${id}`, {
      method: "DELETE",
    });
    return this.getData(response);
  }

  // ============== NOTIFICATION PREFERENCES ==============
  async getNotificationPreferences() {
    const response = await this.request("/api/notification-preferences");
    return this.getData(response);
  }

  async updateNotificationPreferences(prefsData: {
    auto_checkout_self?: number;
    auto_checkout_team?: number;
  }) {
    const response = await this.request("/api/notification-preferences", {
      method: "PUT",
      body: JSON.stringify(prefsData),
    });
    return this.getData(response);
  }

  // ============== ATTENDANCE METRICS & CELEBRATIONS ==============
  async getAttendanceMetrics() {
    const response = await this.request("/api/attendance/metrics");
    return this.getData(response);
  }

  async getCelebrations() {
    const response = await this.request("/api/users/celebrations");
    return this.getData(response);
  }

  // ============== PAYROLL ==============
  async getPayslips(userId?: string) {
    const params = userId ? `?user_id=${userId}` : "";
    const response = await this.request(`/api/payroll/payslips${params}`);
    return this.getData(response);
  }

  async createPayslip(payslipData: any) {
    const response = await this.request("/api/payroll/payslips", {
      method: "POST",
      body: JSON.stringify(payslipData),
    });
    return this.getData(response);
  }

  async getBenefitPlans() {
    const response = await this.request("/api/payroll/benefits");
    return this.getData(response);
  }

  async getBenefitEnrollments(userId?: string) {
    const params = userId ? `?user_id=${userId}` : "";
    const response = await this.request(`/api/payroll/benefits/enrollments${params}`);
    return this.getData(response);
  }

  async enrollInBenefit(enrollmentData: any) {
    const response = await this.request("/api/payroll/benefits/enrollments", {
      method: "POST",
      body: JSON.stringify(enrollmentData),
    });
    return this.getData(response);
  }

  async getExpenseClaims(status?: string) {
    const params = status ? `?status=${status}` : "";
    const response = await this.request(`/api/payroll/expenses${params}`);
    return this.getData(response);
  }

  async createExpenseClaim(expenseData: any) {
    const response = await this.request("/api/payroll/expenses", {
      method: "POST",
      body: JSON.stringify(expenseData),
    });
    return this.getData(response);
  }

  async updateExpenseClaim(id: string, updateData: any) {
    const response = await this.request(`/api/payroll/expenses/${id}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
    return this.getData(response);
  }

  // ============== RECRUITMENT ==============
  async getJobPostings(filters?: { status?: string; department?: string }) {
    const params = new URLSearchParams(filters as any).toString();
    const response = await this.request(`/api/recruitment/jobs${params ? `?${params}` : ""}`);
    return this.getData(response);
  }

  async createJobPosting(jobData: any) {
    const response = await this.request("/api/recruitment/jobs", {
      method: "POST",
      body: JSON.stringify(jobData),
    });
    return this.getData(response);
  }

  async getCandidates(filters?: { job_id?: string; stage?: string; status?: string }) {
    const params = new URLSearchParams(filters as any).toString();
    const response = await this.request(`/api/recruitment/candidates${params ? `?${params}` : ""}`);
    return this.getData(response);
  }

  async createCandidate(candidateData: any) {
    const response = await this.request("/api/recruitment/candidates", {
      method: "POST",
      body: JSON.stringify(candidateData),
    });
    return this.getData(response);
  }

  async getInterviews(filters?: { candidate_id?: string; status?: string }) {
    const params = new URLSearchParams(filters as any).toString();
    const response = await this.request(`/api/recruitment/interviews${params ? `?${params}` : ""}`);
    return this.getData(response);
  }

  async scheduleInterview(interviewData: any) {
    const response = await this.request("/api/recruitment/interviews", {
      method: "POST",
      body: JSON.stringify(interviewData),
    });
    return this.getData(response);
  }

  // ============== LMS (Learning Management) ==============
  async getCourses(filters?: { category?: string; type?: string; status?: string }) {
    const params = new URLSearchParams(filters as any).toString();
    const response = await this.request(`/api/lms/courses${params ? `?${params}` : ""}`);
    return this.getData(response);
  }

  async getCourseEnrollments(userId?: string, status?: string) {
    const params = new URLSearchParams({ user_id: userId || "", status: status || "" } as any).toString();
    const response = await this.request(`/api/lms/enrollments${params ? `?${params}` : ""}`);
    return this.getData(response);
  }

  async enrollInCourse(enrollmentData: any) {
    const response = await this.request("/api/lms/enrollments", {
      method: "POST",
      body: JSON.stringify(enrollmentData),
    });
    return this.getData(response);
  }

  async updateCourseProgress(enrollmentId: string, progressData: any) {
    const response = await this.request(`/api/lms/enrollments/${enrollmentId}`, {
      method: "PUT",
      body: JSON.stringify(progressData),
    });
    return this.getData(response);
  }
}


// Lazy singleton pattern - initialize only when accessed
let _instance: FlowHRAPI | null = null;

export function getAPI(): FlowHRAPI {
  try {
    if (!_instance) {
      _instance = new FlowHRAPI();
    }
    return _instance;
  } catch (error) {
    console.error("[API] Failed to get API instance:", error);
    // Return a new instance anyway to avoid complete failure
    return new FlowHRAPI();
  }
}

// For backward compatibility, provide a default export
export const api = {
  get login() {
    return getAPI().login.bind(getAPI());
  },
  get register() {
    return getAPI().register.bind(getAPI());
  },
  get logout() {
    return getAPI().logout.bind(getAPI());
  },
  get forgotPassword() {
    return getAPI().forgotPassword.bind(getAPI());
  },
  get resetPassword() {
    return getAPI().resetPassword.bind(getAPI());
  },
  get getUsers() {
    return getAPI().getUsers.bind(getAPI());
  },
  get getUser() {
    return getAPI().getUser.bind(getAPI());
  },
  get createUser() {
    return getAPI().createUser.bind(getAPI());
  },
  get updateUser() {
    return getAPI().updateUser.bind(getAPI());
  },
  get updateUserRole() {
    return getAPI().updateUserRole.bind(getAPI());
  },
  get deleteUser() {
    return getAPI().deleteUser.bind(getAPI());
  },
  get activateUser() {
    return getAPI().activateUser.bind(getAPI());
  },
  get changePassword() {
    return getAPI().changePassword.bind(getAPI());
  },
  get adminResetPassword() {
    return getAPI().adminResetPassword.bind(getAPI());
  },
  get getAttendance() {
    return getAPI().getAttendance.bind(getAPI());
  },
  get getTeamAttendance() {
    return getAPI().getTeamAttendance.bind(getAPI());
  },
  get checkIn() {
    return getAPI().checkIn.bind(getAPI());
  },
  get checkOut() {
    return getAPI().checkOut.bind(getAPI());
  },
  get getLeaveRequests() {
    return getAPI().getLeaveRequests.bind(getAPI());
  },
  get createLeaveRequest() {
    return getAPI().createLeaveRequest.bind(getAPI());
  },
  get updateLeaveStatus() {
    return getAPI().updateLeaveStatus.bind(getAPI());
  },
  get deleteLeaveRequest() {
    return getAPI().deleteLeaveRequest.bind(getAPI());
  },
  get getTimesheets() {
    return getAPI().getTimesheets.bind(getAPI());
  },
  get createTimesheetEntry() {
    return getAPI().createTimesheetEntry.bind(getAPI());
  },
  get updateTimesheetEntry() {
    return getAPI().updateTimesheetEntry.bind(getAPI());
  },
  get submitTimesheet() {
    return getAPI().submitTimesheet.bind(getAPI());
  },
  get submitTimesheetWeek() {
    return getAPI().submitTimesheetWeek.bind(getAPI());
  },
  get reviewTimesheet() {
    return getAPI().reviewTimesheet.bind(getAPI());
  },
  get deleteTimesheetEntry() {
    return getAPI().deleteTimesheetEntry.bind(getAPI());
  },
  notifications: {
    list: () => getAPI().getNotifications(),
    markRead: (id: string) => getAPI().markNotificationRead(id),
  },
  // Shift Management
  get getShifts() {
    return getAPI().getShifts.bind(getAPI());
  },
  get createShift() {
    return getAPI().createShift.bind(getAPI());
  },
  get updateShift() {
    return getAPI().updateShift.bind(getAPI());
  },
  get deleteShift() {
    return getAPI().deleteShift.bind(getAPI());
  },
  // Shift Assignments
  get getShiftAssignments() {
    return getAPI().getShiftAssignments.bind(getAPI());
  },
  get createShiftAssignment() {
    return getAPI().createShiftAssignment.bind(getAPI());
  },
  get updateShiftAssignment() {
    return getAPI().updateShiftAssignment.bind(getAPI());
  },
  get deleteShiftAssignment() {
    return getAPI().deleteShiftAssignment.bind(getAPI());
  },
  // Notification Preferences
  get getNotificationPreferences() {
    return getAPI().getNotificationPreferences.bind(getAPI());
  },
  get updateNotificationPreferences() {
    return getAPI().updateNotificationPreferences.bind(getAPI());
  },
  // Auth Token Management
  get refreshToken() {
    return getAPI().refreshToken.bind(getAPI());
  },
  get hasToken() {
    return getAPI().hasToken.bind(getAPI());
  },
  get setToken() {
    return getAPI().setToken.bind(getAPI());
  },
  // Attendance Metrics & Celebrations
  get getAttendanceMetrics() {
    return getAPI().getAttendanceMetrics.bind(getAPI());
  },
  get getCelebrations() {
    return getAPI().getCelebrations.bind(getAPI());
  },
  // Payroll
  get getPayslips() {
    return getAPI().getPayslips.bind(getAPI());
  },
  get createPayslip() {
    return getAPI().createPayslip.bind(getAPI());
  },
  get getBenefitPlans() {
    return getAPI().getBenefitPlans.bind(getAPI());
  },
  get getBenefitEnrollments() {
    return getAPI().getBenefitEnrollments.bind(getAPI());
  },
  get enrollInBenefit() {
    return getAPI().enrollInBenefit.bind(getAPI());
  },
  get getExpenseClaims() {
    return getAPI().getExpenseClaims.bind(getAPI());
  },
  get createExpenseClaim() {
    return getAPI().createExpenseClaim.bind(getAPI());
  },
  get updateExpenseClaim() {
    return getAPI().updateExpenseClaim.bind(getAPI());
  },
  // Recruitment
  get getJobPostings() {
    return getAPI().getJobPostings.bind(getAPI());
  },
  get createJobPosting() {
    return getAPI().createJobPosting.bind(getAPI());
  },
  get getCandidates() {
    return getAPI().getCandidates.bind(getAPI());
  },
  get createCandidate() {
    return getAPI().createCandidate.bind(getAPI());
  },
  get getInterviews() {
    return getAPI().getInterviews.bind(getAPI());
  },
  get scheduleInterview() {
    return getAPI().scheduleInterview.bind(getAPI());
  },
  // LMS (Learning Management)
  get getCourses() {
    return getAPI().getCourses.bind(getAPI());
  },
  get getCourseEnrollments() {
    return getAPI().getCourseEnrollments.bind(getAPI());
  },
  get enrollInCourse() {
    return getAPI().enrollInCourse.bind(getAPI());
  },
  get updateCourseProgress() {
    return getAPI().updateCourseProgress.bind(getAPI());
  },
};
